<template>
  <div class="relative">
    <CustomInput
      :label="label"
      :placeholder="placeholder"
      :inputClass="inputClass"
      :required="required"
      :modelValue="modelValue"
      @update:modelValue="handleInput"
      @focus="showSuggestions = true"
      @blur="handleBlur"
    />
    <div
      v-if="showSuggestions && suggestions.length > 0"
      class="absolute !mb-5 w-full bg-white dark:bg-surface-secondary-dark border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto z-[9999]"
      style="z-index: 9999 !important"
    >
      <div
        v-for="(suggestion, index) in suggestions"
        :key="suggestion.id"
        class="px-1 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
        :class="{
          'bg-gray-100 dark:bg-gray-700': index === selectedIndex,
        }"
        @mousedown="selectSuggestion(suggestion)"
        @mouseenter="selectedIndex = index"
      >
        <div class="font-medium text-text-primary dark:text-dark-secondary">
          {{ suggestion.rut }}
        </div>
        <div class="text-xs text-text-secondary dark:text-dark-secondary">
          {{ suggestion.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineAsyncComponent, ref } from "vue";

const CustomInput = defineAsyncComponent(() => import("../ui/CustomInput.vue"));

import { ClientSearchService } from "../../service/searchClient/clientSearchService";
import { ClientData } from "../../service/searchClient/type";

interface Props {
  label?: string;
  placeholder?: string;
  inputClass?: string;
  required?: boolean;
  modelValue?: string;
}

interface Emits {
  "update:modelValue": [value: string];
  clientSelected: [client: ClientData];
}

const props = withDefaults(defineProps<Props>(), {
  label: "",
  placeholder: "",
  inputClass: "",
  required: false,
  modelValue: "",
});

const emit = defineEmits<Emits>();
const clientSearchServices = new ClientSearchService();
const suggestions = ref<ClientData[]>([]);
const showSuggestions = ref(false);
const selectedIndex = ref(-1);
const isLoading = ref(false);
const isSelecting = ref(false);

let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const formatRut = (value: string): string => {
  const cleaned = value.replace(/[^0-9kK]/g, "").toUpperCase();

  if (cleaned.length <= 1) return cleaned;

  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);

  let formatted = "";
  for (let i = body.length - 1, j = 0; i >= 0; i--, j++) {
    if (j > 0 && j % 3 === 0) {
      formatted = "." + formatted;
    }
    formatted = body[i] + formatted;
  }

  return formatted + "-" + dv;
};

const handleInput = async (value: string) => {
  const formattedValue = formatRut(value);
  emit("update:modelValue", formattedValue);

  if (isSelecting.value) {
    isSelecting.value = false;
    return;
  }

  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  searchTimeout = setTimeout(async () => {
    const cleanValue = value.replace(/[^0-9]/g, "");
    if (cleanValue.length >= 2) {
      isLoading.value = true;
      try {
        suggestions.value = await clientSearchServices.searchClientsByRutAsync(
          value,
          8
        );
        showSuggestions.value = true;
        selectedIndex.value = -1;
      } catch (error) {
        console.error("Error searching clients by RUT:", error);
        suggestions.value = [];
      } finally {
        isLoading.value = false;
      }
    } else {
      suggestions.value = [];
      showSuggestions.value = false;
    }
  }, 300);
};

const selectSuggestion = (client: ClientData) => {
  isSelecting.value = true;
  emit("update:modelValue", client.rut);
  emit("clientSelected", client);
  suggestions.value = [];
  showSuggestions.value = false;
  selectedIndex.value = -1;
};

const handleBlur = () => {
  // Delay para permitir click en sugerencias
  setTimeout(() => {
    showSuggestions.value = false;
    selectedIndex.value = -1;
  }, 200);
};

// Manejo de teclado para navegación
const handleKeydown = (event: KeyboardEvent) => {
  if (!showSuggestions.value || suggestions.value.length === 0) return;

  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();
      selectedIndex.value = Math.min(
        selectedIndex.value + 1,
        suggestions.value.length - 1
      );
      break;
    case "ArrowUp":
      event.preventDefault();
      selectedIndex.value = Math.max(selectedIndex.value - 1, -1);
      break;
    case "Enter":
      event.preventDefault();
      if (selectedIndex.value >= 0) {
        selectSuggestion(suggestions.value[selectedIndex.value]);
      }
      break;
    case "Escape":
      showSuggestions.value = false;
      selectedIndex.value = -1;
      break;
  }
};

// Agregar listener de teclado
if (typeof window !== "undefined") {
  window.addEventListener("keydown", handleKeydown);
}

// Agregar al final del script setup
const setSelecting = (value: boolean) => {
  isSelecting.value = value;
};

// Exponer el método
defineExpose({
  setSelecting,
});
</script>
